import { useState } from "react";
import { concludeSession, SessionConclusionResponse } from "../services/api";

interface SessionConclusionDialogProps {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: SessionConclusionResponse) => void;
}

export function SessionConclusionDialog({
  conversationId,
  isOpen,
  onClose,
  onSuccess,
}: SessionConclusionDialogProps) {
  const [conclusionStatus, setConclusionStatus] = useState<"resolved" | "escalated" | "inconclusive" | null>(
    null
  );
  const [reason, setReason] = useState("");
  const [helpful, setHelpful] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!conclusionStatus || !reason.trim()) {
      setError("Please select a conclusion status and provide a reason");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await concludeSession(conversationId, {
        conversation_id: conversationId,
        conclusion_status: conclusionStatus,
        conclusion_reason: reason.trim(),
        helpful,
      });

      onSuccess?.(result);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to conclude session");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConclusionStatus(null);
    setReason("");
    setHelpful(true);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white shadow-lg">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">End Session</h2>
          <p className="mt-1 text-sm text-gray-500">Wrap up this troubleshooting session</p>
        </div>

        <div className="space-y-4 px-6 py-4">
          <div>
            <label className="text-sm font-semibold text-gray-600 mb-3 block">
              Session Resolution
            </label>
            <div className="space-y-2">
              {(
                [
                  { value: "resolved", label: "✓ Resolved — Problem is fixed", color: "emerald" },
                  { value: "escalated", label: "→ Escalated — Needs higher support", color: "amber" },
                  { value: "inconclusive", label: "? Inconclusive — Status unclear", color: "gray" },
                ] as const
              ).map(({ value, label, color }) => (
                <button
                  key={value}
                  onClick={() => setConclusionStatus(value)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition font-medium text-sm ${
                    conclusionStatus === value
                      ? `border-${color}-500 bg-${color}-50 text-${color}-900`
                      : `border-gray-200 bg-white text-gray-700 hover:border-gray-300`
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-600 mb-2 block">
              Why this conclusion? *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="E.g., 'Applied the workaround and system is now working' or 'Issue requires specialist review'"
              className="w-full px-3 py-2 rounded border border-gray-300 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent"
              rows={3}
            />
          </div>

          <div className="flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-3 border border-blue-200">
            <input
              type="checkbox"
              checked={helpful}
              onChange={(e) => setHelpful(e.target.checked)}
              className="rounded"
              id="helpful-check"
            />
            <label htmlFor="helpful-check" className="text-sm text-gray-700">
              This chat session was helpful overall
            </label>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 border border-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !conclusionStatus}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-brand-accent text-white hover:opacity-90 disabled:opacity-50 transition"
          >
            {loading ? "Concluding..." : "Conclude Session"}
          </button>
        </div>
      </div>
    </div>
  );
}
